import React from 'react';
import { CheckIcon, LockClosedIcon } from '@heroicons/react/solid';

/**
 * Side navigation bar for the Diagnostic Decision module.
 *
 * This component is TOC-only and mirrors the visual design of PADSideNavbar.
 * It displays stages in order: MDT Review → Decision for ADHD →
 * Decision on Differentials (when applicable) → Final Formulation.
 * The differentials step is locked until the ADHD decision form is valid.
 *
 * Props:
 *   sections         — Array built by buildDDNavSections() from diagnosticDecisionRouting.js.
 *                      Each item: { sectionTitle, isUnlocked, status }
 *   currentSectionIndex — Zero-based index of the currently active page
 *                         (0 = MDT Review, 1 = ADHD, 2 = Differentials or Formulation).
 *   navigateToSection — Callback invoked with the target section index.
 *                       The caller is responsible for flushing pending writes
 *                       before navigation where required.
 *   isNavigating     — When true, the entire sidebar is dimmed and non-interactive,
 *                       preventing double-clicks during an in-flight navigation.
 */
function DDSideNavbar({ sections, currentSectionIndex, navigateToSection, isNavigating = false }) {
  return (
    <div className="w-fit ml-8 mt-8 h-full p-5 bg-gray-50 rounded-2xl border border-gray-200 flex-col justify-start items-start gap-4 inline-flex sticky top-8 shrink-0">
      <div className="self-stretch text-gray-500 text-sm font-bold leading-tight">Stages</div>
      <div className={`text-left self-stretch flex-col justify-start items-start gap-1 flex overflow-y-auto ${isNavigating ? 'opacity-60 pointer-events-none' : ''}`}>
        {sections.map((section, index) => {
          const isActive    = currentSectionIndex === index;
          const isCompleted = section.status === 'completed';
          const isLocked    = !section.isUnlocked;

          return (
            <div
              key={index}
              className={`self-stretch h-9 px-3 py-2 
                transition-all duration-300 ease-in-out
                ${isActive ? 'font-bold text-gray-900 border-l-2 border-violet-500' : 'border-l-2 border-transparent'} 
                ${isCompleted ? 'bg-emerald-50' : ''} 
                ${isLocked || isNavigating ? 'bg-gray-200 rounded-lg cursor-not-allowed' : 'cursor-pointer hover:bg-gray-100'} 
                justify-start items-center gap-2 inline-flex`}
              onClick={() => !isLocked && !isNavigating && navigateToSection(index)}
            >
              <div className={`grow shrink basis-0 whitespace-nowrap
                transition-colors duration-300 ease-in-out
                ${isActive ? 'text-gray-900' : 'text-gray-500'} 
                text-sm font-medium leading-tight`}>
                {section.sectionTitle}
              </div>
              <div className="transition-opacity duration-300 ease-in-out">
                {isLocked ? (
                  <LockClosedIcon className="w-4 h-4 text-gray-500" aria-hidden="true" />
                ) : isCompleted && (
                  <CheckIcon className="w-4 h-4 text-green-500" aria-hidden="true" />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default DDSideNavbar;
